import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { LlmUsageApiService, LlmUsageDetail } from 'src/app/core/services/llm-usage-api.service';
import { formatUsd } from '../llm-cost.filters';

/**
 * One LLM call, in full.
 *
 * Shows what was spent and on what — never the prompt, the model's response, or any customer
 * field. This is a KYC platform; a cost screen is not a reason to surface what was sent to a
 * model, and the API deliberately does not return it.
 */
@Component({
  selector: 'app-call-detail-drawer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './call-detail-drawer.component.html',
})
export class CallDetailDrawerComponent implements OnChanges {
  private readonly api = inject(LlmUsageApiService);

  /** Call id to show; null closes the drawer. */
  @Input() callId: number | null = null;
  @Output() closed = new EventEmitter<void>();

  readonly formatUsd = formatUsd;

  detail: LlmUsageDetail | null = null;
  loading = false;
  error = '';

  ngOnChanges(): void {
    if (this.callId == null) {
      this.detail = null;
      return;
    }
    this.load(this.callId);
  }

  private load(id: number): void {
    this.loading = true;
    this.error = '';
    this.detail = null;
    this.api
      .get(id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => (this.detail = res.data ?? null),
        error: (err) => (this.error = resolveApiErrorMessage(err) || 'Could not load this call.'),
      });
  }

  close(): void {
    this.closed.emit();
  }

  statusClass(status: string): string {
    if (status === 'SUCCESS') return 'badge bg-success-light text-success';
    if (status === 'FAILED') return 'badge bg-danger-light text-danger';
    return 'badge bg-warning-light text-warning';
  }

  /** A timed-out call may have been billed anyway; say so where someone is reading the cost. */
  get statusNote(): string | null {
    if (this.detail?.status === 'UNKNOWN_BILLED') {
      return 'Timed out before a response arrived — the provider may still have billed this call.';
    }
    return null;
  }
}
